import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { catchError, firstValueFrom, of } from 'rxjs';
import {
  BookingServiceService,
  EmployeeService,
  RoomService
} from '@beautyapp/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { InputModule } from '@beautyapp/form';
import { BookingServiceFormComponent } from './components/booking-service-form/booking-service-form.component';
import {
  AddBookingServiceModalData,
  BookingFormValue,
  BookingServiceOutputData
} from './types';
import { NzSpinComponent } from 'ng-zorro-antd/spin';

/** Component operation mode */
type ComponentMode = 'add' | 'edit' | 'output-add' | 'output-edit';

/** Initialization state */
type InitState = 'pending' | 'loading' | 'ready';

@Component({
  selector: 'beautyapp-add-booking-service',
  standalone: true,
  imports: [
    NzModalModule,
    NzButtonComponent,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    InputModule,
    BookingServiceFormComponent,
    NzSpinComponent
  ],
  templateUrl: './add-booking-service.component.html',
  styleUrl: './add-booking-service.component.scss'
})
export class AddBookingServiceComponent implements OnInit {
  @ViewChild(BookingServiceFormComponent) formComponent!: BookingServiceFormComponent;

  // Services
  private destroyRef = inject(DestroyRef);
  protected data: AddBookingServiceModalData = inject(NZ_MODAL_DATA);
  protected modalRef = inject(NzModalRef);
  private bookingServiceService = inject(BookingServiceService);
  private employeeService = inject(EmployeeService);
  private roomService = inject(RoomService);
  private permissionsService = inject(NgxPermissionsService);

  // ============ STATE ============

  /** Initialization state */
  readonly initState = signal<InitState>('pending');

  /** Current component mode */
  readonly mode = signal<ComponentMode>(this.determineMode());

  /** Loading state for submit */
  readonly loading = signal(false);

  /** View-only mode - disables all editing */
  readonly viewOnly = signal(!!this.data.viewOnly);

  /** Current room data */
  readonly room = signal<any | null>(null);

  /** List of employees for dropdown */
  readonly employees = signal<any[]>(this.data.employees || []);

  /** Current form value */
  readonly formValue = signal<BookingFormValue | null>(null);

  /** Form validity */
  readonly formValid = signal(false);

  /** Selected service */
  readonly service = signal<any | null>(null);

  /** Service products */
  readonly serviceProducts = signal<any[]>([]);

  /** Spec services */
  readonly specServices = signal<any[]>([]);

  /** Show form (after initialization) */
  readonly showForm = signal(false);

  // ============ COMPUTED ============

  /** Check if component is in edit mode */
  readonly isEditMode = computed(() => this.mode() === 'edit' || this.mode() === 'output-edit');

  /** Check if component is in output mode */
  readonly isOutputMode = computed(() => this.mode() === 'output-add' || this.mode() === 'output-edit');

  /** Check if user is employee-level (no calendar.view_all permission) */
  readonly isEmployee = computed(() => !this.permissionsService.getPermission('calendar.view_all'));

  /** Initial data for form component */
  readonly initialData = computed(() => this.data.bookingService || null);

  /** Booking ID - from data or booking object */
  readonly bookingId = computed(() => this.data.bookingId || this.data.booking?.id || null);

  /** User ID - for package service lookup */
  readonly userId = computed(() => this.data.userId || this.data.booking?.user?.id || null);

  readonly employeeId = linkedSignal(() => this.data.employeeId || this.data.booking?.employeeId || null);

  employeeResource = rxResource({
    params: () => ({employeeId: this.employeeId()}),
    stream: ({params}) => {
      if (!params.employeeId) {
        return of(null);
      }
      return this.employeeService.getById(params.employeeId).pipe(
        catchError(() => of(null))
      );
    }
  })

  employee = computed(() => this.employeeResource.value())

  // ============ LIFECYCLE ============

  async ngOnInit() {
    console.log('ngOnInit', this.data);
    console.log('initialData', this.initialData());
    if (this.data.booking) {
      await this.initializeBookingCreation();
    }

    if (this.data.bookingService){
      await this.initializeBookingEdit()
    }
  }

  // ============ INITIALIZATION ============

  private async initializeBookingCreation(): Promise<void> {
    this.initState.set('loading');

    try {
      console.log('initializing...', this.data);
      // Extract from booking if provided
      const booking = this.data.booking;
      const employeeId = this.data.employeeId || booking?.employee?.id;
      const roomId = this.data.roomId || booking?.roomId;

      // Load room data
      if (roomId) {
        await this.loadRoom(roomId);
      }

      // Set employee data - use passed employee or load from API
      if (employeeId) {
        this.employeeId.set(employeeId);
      }

      this.showForm.set(true);
      this.initState.set('ready');
    } catch (error) {
      console.error('Initialization error:', error);
      this.initState.set('ready');
    }
  }

  private async initializeBookingEdit(): Promise<void> {
    this.initState.set('loading');

    try {


    console.log('initializing...', this.data);
    console.log('initializing...', this.data.employeeId);

    const employeeId = this.data.employeeId;

    this.employeeId.set(employeeId);
    this.showForm.set(true);
    this.initState.set('ready');
    } catch (error) {
      console.error('Initialization error:', error);
      this.initState.set('ready');
    }

  }

  private async loadRoom(roomId: string): Promise<void> {
    try {
      const room = await firstValueFrom(this.roomService.getById(roomId));
      this.room.set(room);
    } catch {
      this.room.set(null);
    }
  }

  private ensureEmployeeInList(employee: any): void {
    if (!employee) return;

    const currentEmployees = this.employees();
    const employeeExists = currentEmployees.some(e => e.id === employee.id);
    if (!employeeExists) {
      this.employees.set([employee, ...currentEmployees]);
    }
  }

  private determineMode(): ComponentMode {
    const hasBookingService = !!this.data.bookingService;
    const isOutput = !!this.data.outputMode;

    if (isOutput) {
      return hasBookingService ? 'output-edit' : 'output-add';
    }
    return hasBookingService ? 'edit' : 'add';
  }

  // ============ FORM EVENTS ============

  onFormValueChange(value: BookingFormValue): void {
    this.formValue.set(value);
  }

  onFormValidChange(valid: boolean): void {
    this.formValid.set(valid);
  }

  onServiceChange(service: any): void {
    this.service.set(service);
  }

  onServiceProductsChange(products: any[]): void {
    this.serviceProducts.set(products);
  }

  onSpecServicesChange(specs: any[]): void {
    this.specServices.set(specs);
  }

  // ============ ACTIONS ============

  cancel(): void {
    this.modalRef.close();
  }

  submit(): void {
    if (!this.formComponent) return;

    this.formComponent.markAllAsTouched();

    console.log('submitting...', this.formComponent.isValid());
    console.log('submitting...', this.formComponent.form);

    if (!this.formComponent.isValid()) return;

    this.loading.set(true);

    const formValue = this.formComponent.getValue();
    const bookingData = this.prepareBookingData(formValue);

    // Output mode - just close modal with data
    if (this.isOutputMode()) {
      const outputData: BookingServiceOutputData = {
        ...bookingData,
        employee: this.employee(),
        service: this.formComponent.getService(),
        specServices: this.formComponent.getSpecServices(),
        serviceProducts: this.formComponent.getServiceProducts()
      };
      this.modalRef.close(outputData);
      this.loading.set(false);
      return;
    }

    console.log('this.isEditMode()', this.isEditMode());

    // API mode - create or update
    const bookingServiceId = this.data.bookingService?.id;
    const request = this.isEditMode() && bookingServiceId
      ? this.bookingServiceService.update(bookingServiceId, { id: bookingServiceId, ...bookingData })
      : this.bookingServiceService.create(bookingData);

    request.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.modalRef.close(true);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Submit error:', error);
        this.loading.set(false);
      }
    });
  }

  private prepareBookingData(formValue: BookingFormValue): any {
    const service = this.formComponent.getService();
    return {
      bookingId: formValue.bookingId || this.bookingId(),
      employeeId: formValue.employeeId,
      serviceId: formValue.serviceId,
      price: +formValue.price! || 0,
      salePrice: +formValue.salePrice!,
      salaryFixed: +formValue.salaryFixed! || null,
      salaryPercent: +formValue.salaryPercent! || null,
      salaryPercentFromPrice: formValue.salaryPercentFromPrice,
      expence: +formValue.expense!,
      // Additional cost — sent to the backend unchanged, never derived (beaut-240).
      additionalExpence: +formValue.additionalExpense! || 0,
      quantity: +formValue.quantity || 1,
      time: formValue.time,
      description: formValue.description,
      specServices: this.formComponent.getSpecServices(),
      serviceProducts: this.formComponent.getServiceProducts(),
      // beaut-433: never send null — the column is NOT NULL and update used to
      // Object.assign it straight through to a 500.
      minutes: formValue.minutes ?? 0,
      additionalEmployees: formValue.additionalEmployees,
      offerPurchaseId: service?.offerPurchaseId || null,
      offerServiceId: service?.offerServiceId || null,
      packagePurchaseId: service?.packagePurchaseId || null,
      packageItemId: service?.packageItemId || null,
    };
  }
}
